import type { MerchantLicenseShareDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { Stack, Skeleton } from "@mui/material";
import { ValuesBox } from "Components/Shared/ValuesBox/ValuesBox";
import { LicenseShareCard } from "Components/Shared/Inputs/HunterList/LicenseShareCard";
import { usePartyLicensesQuery } from "Api/Queries/Licenses/usePartyLicensesQuery";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { ErrorText } from "Components/Shared/Errors/ErrorText";

const COMPONENT_RESOURCES = Resources.Components.Shared.HunterList;

type Props = { licenseShares: Array<MerchantLicenseShareDto> };

export const LicenseShares = ({ licenseShares }: Props) => {
  const { t } = useResource();
  const user = useLoggedInUser();
  const {
    data: licensesData,
    isLoading,
    isError,
  } = usePartyLicensesQuery(user?.partyPublicID);

  if (isLoading) {
    return (
      <ValuesBox title={t(Resources.Merchant.LicenseShares.Title)}>
        <Skeleton height={45} variant="rounded" />
      </ValuesBox>
    );
  }

  if (isError || !licensesData) {
    return (
      <ValuesBox title={t(Resources.Merchant.LicenseShares.Title)}>
        <ErrorText>{t(Resources.Components.Merchant.LoadingError)}</ErrorText>
      </ValuesBox>
    );
  }

  const licenses = licensesData.items;
  const primaryLicenseShare = licenseShares.find((ls) => ls.isPrimary);
  const sortedLicenseShares = licenseShares
    .filter((licenseShare) => !licenseShare.isPrimary)
    .sort((a, b) => b.sharePercentage - a.sharePercentage);

  return (
    <ValuesBox title={t(Resources.Merchant.LicenseShares.Title)}>
      {primaryLicenseShare && (
        <LicenseShareCard
          isPrimary
          licenseNo={primaryLicenseShare.licenseNumber}
          commission={primaryLicenseShare.sharePercentage.toFixed(2)}
          companyName={primaryLicenseShare.holderName}
        />
      )}
      <Stack gap={1}>
        {sortedLicenseShares.map(
          ({ licensePublicID, licenseNumber, sharePercentage, holderName }) => {
            const isCurrentUsers = licenses.some(
              ({ publicID }) => publicID === licensePublicID,
            );
            const companyName = isCurrentUsers
              ? t(COMPONENT_RESOURCES.MainHunterCard.YourCommission)
              : holderName;

            return (
              <LicenseShareCard
                key={licensePublicID}
                licenseNo={licenseNumber}
                commission={sharePercentage.toFixed(2)}
                companyName={companyName}
              />
            );
          },
        )}
      </Stack>
    </ValuesBox>
  );
};
